import React, { useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Container
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import faqData from '../data/faqData';

// Add Google Fonts for luxury typography
const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
`;

// Inject the font styles
if (typeof document !== 'undefined' && !document.head.querySelector('[data-faq-fonts]')) {
  const styleElement = document.createElement('style');
  styleElement.textContent = fontImport;
  styleElement.setAttribute('data-faq-fonts', 'true');
  document.head.appendChild(styleElement);
}

const MotionBox = motion(Box);
const MotionAccordionItem = motion(AccordionItem);

const FAQSection = ({ 
  showTitle = true, 
  maxWidth = "container.lg", 
  spacing = 6,
  variant = "default" // "default" for standalone, "compact" for embedded
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const handleAccordionChange = (expandedIndex) => {
    const newExpanded = new Set(expandedItems);
    if (Array.isArray(expandedIndex)) {
      // Multiple items can be expanded
      expandedIndex.forEach(index => {
        if (newExpanded.has(index)) {
          newExpanded.delete(index);
        } else {
          newExpanded.add(index);
        }
      });
    } else {
      // Single item toggle
      if (newExpanded.has(expandedIndex)) {
        newExpanded.delete(expandedIndex);
      } else {
        newExpanded.add(expandedIndex);
      }
    }
    setExpandedItems(newExpanded);
  };

  const isCompact = variant === "compact";

  return (
    <Container maxW={maxWidth} px={0}>
      {showTitle && !isCompact && (
        <MotionBox
          textAlign="center"
          mb={{ base: 8, lg: 12 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Heading
            as="h2"
            size={{ base: "xl", lg: "2xl" }}
            fontFamily="'Playfair Display', serif"
            fontWeight="600"
            color="gray.800"
            mb={4}
          >
            Frequently Asked Questions
          </Heading>
          <Text
            fontSize={{ base: "md", lg: "lg" }}
            color="gray.600"
            fontFamily="'Inter', sans-serif"
            maxW="600px"
            mx="auto"
            lineHeight="1.6"
          >
            Find answers to the most common questions about our jewelry, materials, and services.
          </Text>
        </MotionBox>
      )}

      <VStack spacing={spacing} align="stretch">
        <Accordion allowMultiple>
          {faqData.map((faq, index) => (
            <MotionAccordionItem
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              mb={4}
              overflow="hidden"
              boxShadow={isCompact ? "md" : "0 4px 20px rgba(0, 0, 0, 0.08)"}
              _hover={{
                boxShadow: isCompact ? "lg" : "0 8px 30px rgba(0, 0, 0, 0.12)",
                transform: "translateY(-2px)"
              }}
              style={{ transition: "all 0.3s ease" }}
            >
              <AccordionButton
                p={{ base: 4, md: 6 }}
                _hover={{ bg: "gray.50" }}
                _expanded={{ 
                  bg: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  borderBottomColor: "gray.300"
                }}
                borderBottom="1px solid"
                borderBottomColor="transparent"
                transition="all 0.3s ease"
              >
                <Box flex="1" textAlign="left" pr={4}>
                  <Heading
                    as="h3"
                    size={isCompact ? "sm" : "md"}
                    fontFamily="'Playfair Display', serif"
                    fontWeight="600"
                    color="gray.800"
                    lineHeight="1.4"
                  >
                    {faq.question}
                  </Heading>
                </Box>
                <AccordionIcon 
                  fontSize={isCompact ? "20px" : "24px"} 
                  color="gray.600" 
                  transition="transform 0.3s ease"
                />
              </AccordionButton>

              <AccordionPanel>
                <AnimatePresence>
                  <MotionBox
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <Box 
                      p={{ base: 4, md: 6 }} 
                      pt={0}
                      bg="linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)"
                    >
                      <Text
                        fontSize={isCompact ? "md" : "lg"}
                        lineHeight="1.7"
                        color="gray.700"
                        fontFamily="'Inter', sans-serif"
                        fontWeight="400"
                      >
                        {faq.answer}
                      </Text>
                    </Box>
                  </MotionBox>
                </AnimatePresence>
              </AccordionPanel>
            </MotionAccordionItem>
          ))}
        </Accordion>
      </VStack>

      {!isCompact && (
        <MotionBox
          textAlign="center"
          mt={{ base: 8, lg: 12 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Box
            bg="linear-gradient(135deg, #f1f3f4 0%, #e8eaed 100%)"
            p={{ base: 6, md: 8 }}
            borderRadius="2xl"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
          >
            <Heading
              as="h3"
              size="lg"
              fontFamily="'Playfair Display', serif"
              fontWeight="600"
              color="gray.800"
              mb={3}
            >
              Still have questions?
            </Heading>
            <Text
              fontSize="lg"
              color="gray.600"
              fontFamily="'Inter', sans-serif"
              mb={4}
              lineHeight="1.6"
            >
              Can't find what you're looking for? Our customer support team is here to help.
            </Text>
            <MotionBox
              as="a"
              href="/contact"
              display="inline-block"
              bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
              color="white"
              px={{ base: 6, md: 8 }}
              py={{ base: 3, md: 4 }}
              borderRadius="xl"
              fontFamily="'Inter', sans-serif"
              fontWeight="600"
              fontSize={{ base: "md", md: "lg" }}
              textDecoration="none"
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)"
              }}
              whileTap={{ scale: 0.95 }}
              boxShadow="0 4px 14px rgba(0, 0, 0, 0.15)"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.1)"
              transition="all 0.3s ease"
              _hover={{
                textDecoration: "none",
                bg: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)"
              }}
            >
              Contact Support
            </MotionBox>
          </Box>
        </MotionBox>
      )}
    </Container>
  );
};

export default FAQSection;